"use client";

import React, { useEffect, useRef, useState } from "react";
import { Reveal } from "@/components/portfolio-review/Reveal";

/**
 * Sample evaluation scorecard — the centerpiece proof section.
 * Illustrative data based on the framework's worked example
 * (Stripe Dashboard Redesign → verdict PROFESSIONAL, 2.2/3).
 * Light "report" card: white scorecard with segmented score meters,
 * on a warm-gray frame that extends into the hiring-signals strip.
 * Scores and meters render at their final values on the server so the HTML
 * never ships zeros (no 0 → value count-up); crawlers and no-JS see real scores.
 */

type Competency = {
  name: string;
  score: number; // 1.0 - 3.0
  note: string;
};

const COMPETENCIES: Competency[] = [
  {
    name: "Craft & Visual Design",
    score: 2.4,
    note: "Polished, consistent UI with a clear grid, with a couple of spacing inconsistencies on secondary screens.",
  },
  {
    name: "User-Centered Research",
    score: 1.8,
    note: "Research is described but few artifacts are shown; no testing evidence or user quotes.",
  },
  {
    name: "Interaction & Systems Thinking",
    score: 2.3,
    note: "Strong flows and states; component reuse is implied but a system isn't made explicit.",
  },
  {
    name: "Communication & Storytelling",
    score: 2.1,
    note: "Problem is framed well; impact is asserted but not quantified.",
  },
];

const OVERALL = 2.2;

const SIGNALS = [
  {
    label: "Green flags",
    color: "#6DA83E",
    text: "Clean, confident visual craft that reads as senior. Clear before/after problem framing.",
  },
  {
    label: "Yellow flags",
    color: "#E0A400",
    text: "Research process claimed but not evidenced. Probe in interview. Impact stated without metrics.",
  },
  {
    label: "Red flags",
    color: "#E0491C",
    text: "No usability testing or user feedback shown.",
  },
];

/** Green / yellow / red band for a 1–3 score. */
function bandColor(score: number): string {
  if (score >= 2.3) return "#6DA83E";
  if (score >= 2.0) return "#E0A400";
  return "#E0491C";
}

/**
 * Renders a score at its final value. Deliberately static: the illustrative
 * numbers must be identical in server HTML and on the client (no 0 → target
 * count-up), so crawlers, no-JS, and slow-JS visitors never see "0.0/3".
 */
const AnimatedScore: React.FC<{
  value: number;
  active?: boolean;
  className?: string;
  style?: React.CSSProperties;
}> = ({ value, className, style }) => (
  <span className={className} style={style}>
    {value.toFixed(1)}
  </span>
);

const TICKS = 20;

/** Segmented meter — TICKS vertical bars; filled bars colorize when active. */
const ScoreMeter: React.FC<{
  score: number;
  color: string;
  active?: boolean;
  delay: number;
}> = ({ score, color, delay }) => {
  const filled = Math.round(((score - 1) / 2) * TICKS); // 1..3 → 0..TICKS
  return (
    <div className="flex items-end gap-[2px]">
      {Array.from({ length: TICKS }).map((_, i) => (
        <span
          key={i}
          className="h-4 w-[3px] rounded-[1px]"
          style={{
            backgroundColor: i < filled ? color : "#D8D7D3",
            transition: "background-color 0.35s ease",
            transitionDelay: `${delay + i * 20}ms`,
          }}
        />
      ))}
    </div>
  );
};

/** Fires `true` the first time the referenced node enters the viewport. */
function useInView() {
  const ref = useRef<HTMLDivElement | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (mql.matches) {
      setInView(true);
      return;
    }
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setInView(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.3 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return { ref, inView };
}

export const PRSampleScorecard: React.FC<{
  eyebrow?: string;
  heading?: string;
  subhead?: string;
  footnote?: string;
  linkLead?: string;
  linkText?: string;
  linkHref?: string;
}> = ({
  eyebrow = "Sample report",
  heading = "This is what you leave with.",
  subhead = "A hiring manager's read of your portfolio, as a PDF action plan. It tells you what to do before your next application.",
  footnote = "Illustrative example. This is the checklist behind every Hiring Manager Portfolio Review, not an automated report.",
  linkLead,
  linkText,
  linkHref,
}) => {
  const { ref, inView } = useInView();

  return (
    <section id="sample-report" className="relative py-16 md:py-24 bg-[#F8F9FB]">
      <div className="max-w-3xl mx-auto px-5 md:px-8">
        <Reveal>
          <div className="text-center mb-10 md:mb-14">
            <p className="text-xs md:text-sm font-semibold tracking-wide uppercase text-[#C2410C] mb-3">
              {eyebrow}
            </p>
            <h2 className="gradient-heading text-3xl md:text-5xl font-prociono font-normal leading-tight tracking-tight">
              {heading}
            </h2>
            <p className="mt-4 text-sm md:text-base text-[#5A4F45] max-w-xl mx-auto">
              {subhead}
            </p>
            {linkHref && (
              <p className="mt-4 text-xs text-[#6E6460]">
                {linkLead}{" "}
                <a
                  href={linkHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-[#2E2523] underline underline-offset-4 decoration-[#C9C5BE] hover:decoration-[#F97316] hover:text-[#F97316] transition"
                >
                  {linkText}
                </a>
              </p>
            )}
          </div>
        </Reveal>

        {/* Report card — warm-gray frame */}
        <div
          ref={ref}
          className="rounded-[28px] bg-[#E4E2DE] p-2 md:p-2.5 shadow-xl"
        >
          {/* White scorecard */}
          <div className="rounded-[22px] bg-white px-6 py-8 md:px-10 md:py-10 shadow-sm">
            {/* Header */}
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-[11px] md:text-xs font-semibold uppercase tracking-[0.12em] text-[#6F6D68]">
                  Case Study
                </p>
                <h3 className="mt-2 md:mt-3 text-2xl md:text-4xl font-prociono text-[#2E2523] leading-tight">
                  Stripe Dashboard Redesign
                </h3>
              </div>
              <div className="shrink-0 text-right">
                <span className="inline-flex items-center rounded-full border border-[#F2D6B6] bg-[#FBEEDD] px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-[#9A4F12]">
                  Professional
                </span>
                <p className="mt-2 md:mt-3 font-prociono text-[#2E2523] leading-none text-4xl md:text-5xl">
                  <AnimatedScore value={OVERALL} active={inView} />
                  <span className="align-super text-lg md:text-xl text-[#6F6D68]">
                    /3
                  </span>
                </p>
              </div>
            </div>

            <div className="mt-6 md:mt-7 border-t border-[#ECEBE8]" />

            {/* Competency grid */}
            <div className="mt-7 md:mt-9 grid grid-cols-1 gap-x-10 gap-y-9 sm:grid-cols-2 md:gap-y-11">
              {COMPETENCIES.map((c, i) => {
                const color = bandColor(c.score);
                return (
                  <div key={c.name}>
                    <h4 className="text-sm md:text-base font-semibold text-[#232220]">
                      {c.name}
                    </h4>
                    <div className="mt-3 flex items-center gap-3">
                      <ScoreMeter
                        score={c.score}
                        color={color}
                        active={inView}
                        delay={i * 120}
                      />
                      <AnimatedScore
                        value={c.score}
                        active={inView}
                        className="text-lg md:text-xl font-semibold"
                        style={{ color }}
                      />
                    </div>
                    <p className="mt-4 text-sm md:text-[15px] text-[#5A5752] leading-relaxed">
                      {c.note}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Hiring signals — on the gray frame */}
          <div className="px-5 pt-6 pb-3 md:px-8 md:pt-8 md:pb-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#5F5D59]">
              Hiring signals
            </p>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-3">
              {SIGNALS.map((s, i) => (
                <div
                  key={s.label}
                  className={
                    i === 0
                      ? "sm:pr-6"
                      : "mt-6 sm:mt-0 sm:border-l sm:border-[#CDCBC7] sm:px-6"
                  }
                >
                  <p className="flex items-center gap-2 text-sm font-medium text-[#232220]">
                    <span
                      className="h-2 w-2 rounded-full"
                      style={{ backgroundColor: s.color }}
                    />
                    {s.label}
                  </p>
                  <p className="mt-4 text-sm text-[#5A5752] leading-relaxed">
                    {s.text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <p className="mt-5 text-center text-xs text-[#7f6d70]">
          {footnote}
        </p>
      </div>
    </section>
  );
};
